import React, {
    useState,
    createContext,
    useContext,
    useEffect,
    useRef,
} from "react"
import useSWR from "swr"
import Progress from "./Progress"
import StepOne from "./step-1"
import StepTwo from "./step-2"
import StepThree from "./step-3"
import StepFour from "./step-4"

declare global {
    interface Window {
        local_werkplaatsafspraak_object: {
            asset: string
            is_corporate: boolean
        }
    }
}

export interface Location {
    street: string
    zipcode: string
    city: string
    email: string
    phone: string
    manager: string
    openinghours: string
    whatsapp?: string
    title: string
}

interface Werkplaatsafspraak {
    locations: Location[]
    use_timepicker: boolean
    blocked_dates: { date: string }[]

    options: {
        title: string
    }[]
}

interface Fields {
    step: 1 | 2 | 3 | 4
    licensePlate?: string
    options: string[]
    location?: string
    date?: string
    time?: string
    timeComment?: string
    form: Form
    isComplete: boolean
}

interface Form {
    firstName?: string
    lastName?: string
    email?: string
    phone?: string
    message?: string
}

interface Context extends Fields {
    setStep: (step: number) => void
    setLicensePlate: (value: string) => void
    setOptions: (value: string[]) => void
    setLocation: (value: string) => void
    setDate: (date: string) => void
    setTime: (value: string) => void
    setTimeComment: (value: string) => void
    setFormField: (key: string, value: string) => void
    setComplete: (value: boolean) => void
}

const FieldsContext = createContext<Context | null>(null)

export function useFields() {
    const context = useContext(FieldsContext)
    if (!context) throw new Error("Hook fired before context")

    return context
}

export default function App() {
    const { data, error } = useSWR<Werkplaatsafspraak>(
        "/wp-json/sites/v1/werkplaatsafspraak",
        (url) => fetch(url).then((res) => res.json())
    )
    const ref = useRef<HTMLDivElement>()

    const [fields, setFields] = useState<Fields>({
        step: 1,
        licensePlate: null,
        options: [],
        date: null,
        time: null,
        timeComment: null,
        form: {
            firstName: null,
            lastName: null,
            email: null,
            phone: null,
            message: null,
        },
        isComplete: false,
    })

    useEffect(() => {
        const queryString = window.location.search
        const urlParams = new URLSearchParams(queryString)
        const licensePlate = urlParams.get("licenseplate")
        handleSetter("licensePlate", licensePlate)
    }, [])

    useEffect(() => {
        if (!ref.current) return

        window.scrollTo({
            behavior: "smooth",
            top: ref.current.offsetTop - 20,
        })
    }, [fields.step])

    if (!data) {
        return <div className="mb-10">Laden...</div>
    }

    if (error) {
        return (
            <div className="mb-10">
                Er is iets fout gegaan met het laden van de werkplaatsmodule.
                Probeer het later nog eens..
            </div>
        )
    }

    function handleSetter(key: keyof Fields, value: any) {
        setFields((prev) => {
            return {
                ...prev,
                [key]: value,
            }
        })
    }

    function handleForm(key: string, value: string) {
        setFields((prev) => {
            return {
                ...prev,
                form: {
                    ...prev.form,
                    [key]: value,
                },
            }
        })
    }

    const { location, minimum_days, options, use_timepicker, blocked_dates } = data

    return (
        <FieldsContext.Provider
            value={{
                ...fields,
                setStep: (value) => handleSetter("step", value),
                setLicensePlate: (value) => handleSetter("licensePlate", value),
                setOptions: (value) => handleSetter("options", value),
                setLocation: (value) => handleSetter("location", value),
                setDate: (value) => handleSetter("date", value),
                setTime: (value) => handleSetter("time", value),
                setTimeComment: (value) => handleSetter("timeComment", value),
                setFormField: (key, value) => handleForm(key, value),
                setComplete: (value) => handleSetter("isComplete", value),
            }}
        >
            {/* @ts-ignore */}
            <Progress ref={ref} />
            <div className="py-5 mb-10 xl:w-3/4 w-full">
                {fields.step === 1 && <StepOne options={options} />}
                {fields.step === 2 && (
                    <StepTwo location={location} minimumDays={minimum_days} useTimeSlots={use_timepicker} blockedDates={blocked_dates} />
                )}
                {fields.step === 3 && <StepThree />}
                {fields.step === 4 && <StepFour location={location} />}
            </div>
        </FieldsContext.Provider>
    )
}
